const mergeFieldValue = (field, formikValues = {}) => {
  if (!field?.name) {
    return field;
  }

  return {
    ...field,
    value: formikValues?.[field.name] ?? field?.value ?? "",
  };
};

const mergeFields = (fields = [], formikValues = {}) => {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.map((field) => ({
    ...mergeFieldValue(field, formikValues),
    options: Array.isArray(field?.options)
      ? field.options.map((option) => ({ ...option }))
      : field?.options,
  }));
};

export const normalizeFieldsToMap = (fields) => {
  if (Array.isArray(fields)) {
    return fields.reduce((acc, field) => {
      if (field?.name) {
        acc[field.name] = field?.value ?? "";
      }

      return acc;
    }, {});
  }

  if (fields && typeof fields === "object") {
    return fields;
  }

  return {};
};

export const getDocumentByFormId = (documents = {}, formId = null) => {
  if (!formId || !documents || typeof documents !== "object") {
    return null;
  }

  return documents?.[formId] || null;
};

export const getDocumentFields = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") {
    return [];
  }

  if (Array.isArray(documentPayload?.sections)) {
    return documentPayload.sections.flatMap((section) => section?.fields || []);
  }

  return Array.isArray(documentPayload?.fields) ? documentPayload.fields : [];
};

export const getDocumentFieldValueByName = (
  documentPayload,
  fieldName,
  fallback = "",
) => {
  if (!fieldName) {
    return fallback;
  }

  const match = getDocumentFields(documentPayload).find(
    (field) => field?.name === fieldName,
  );

  return match?.value ?? fallback;
};

export const getDocumentFieldValueByNames = (
  documentPayload,
  fieldNames = [],
  fallback = "",
) => {
  if (!Array.isArray(fieldNames) || fieldNames.length === 0) {
    return fallback;
  }

  for (const fieldName of fieldNames) {
    const value = getDocumentFieldValueByName(
      documentPayload,
      fieldName,
      undefined,
    );
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

export const getDocumentFieldValueByFieldId = (
  documentPayload,
  fieldId,
  fallback = "",
) => {
  if (!fieldId) {
    return fallback;
  }

  const match = getDocumentFields(documentPayload).find(
    (field) => field?.field_id === fieldId,
  );

  return match?.value ?? fallback;
};

export const getAllDocumentFields = (documents = {}, excludeFormId = null) => {
  if (!documents || typeof documents !== "object") {
    return [];
  }

  return Object.entries(documents).flatMap(([formId, documentPayload]) => {
    if (excludeFormId && formId === excludeFormId) {
      return [];
    }

    return getDocumentFields(documentPayload).map((field) => ({
      ...field,
      __formId: formId,
    }));
  });
};

export const getSharedFieldValueFromDocuments = ({
  documents = {},
  fieldId = null,
  fieldName = null,
  excludeFormId = null,
} = {}) => {
  const allFields = getAllDocumentFields(documents, excludeFormId);

  const match = allFields.find((field) => {
    if (fieldId && field?.field_id === fieldId) {
      return (
        field?.value !== undefined &&
        field?.value !== null &&
        field?.value !== ""
      );
    }

    if (fieldName && field?.name === fieldName) {
      return (
        field?.value !== undefined &&
        field?.value !== null &&
        field?.value !== ""
      );
    }

    return false;
  });

  return match?.value ?? "";
};

export const buildResolvedDocumentPayload = (
  selectedDocument,
  documents = {},
  formikValues = {},
) => {
  if (!selectedDocument?.form_id) {
    return null;
  }

  const currentDocument = getDocumentByFormId(
    documents,
    selectedDocument.form_id,
  );

  if (!currentDocument) {
    return null;
  }

  if (Array.isArray(currentDocument?.sections)) {
    return {
      ...currentDocument,
      sections: currentDocument.sections.map((section) => ({
        ...section,
        fields: mergeFields(section?.fields, formikValues),
      })),
    };
  }

  return {
    ...currentDocument,
    fields: mergeFields(currentDocument?.fields, formikValues),
  };
};

export const extractFlatValuesFromDocumentPayload = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") {
    return {};
  }

  const fields = getDocumentFields(documentPayload);

  return fields.reduce((acc, field) => {
    if (!field?.name) {
      return acc;
    }

    acc[field.name] = field?.value ?? "";
    return acc;
  }, {});
};
